import { Cancel, Delete, Edit, Event, Expand, Groups2, LineStyle, LocationOn, OpenInFull, Subject } from "@mui/icons-material";
import { Alert, Box, Button, Card, CardActions, CardContent, CardHeader, IconButton, Link, Stack, TextField, Tooltip, Typography } from "@mui/material";
import Editor from "../TextEditor/Editor";
import { TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useAuth } from "../../context/authContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Actions } from "../../Redux/Reducer/rootReducer";
import { CalendarEvent, UserType } from "../../Types/UnitFeatureTypes";
import DatePickerComponent from "./CustomDatePicker";
import { Dispatch, SetStateAction, useState } from "react";
import { useApp } from "../../context/appContext";


interface EventCardProps extends CalendarEvent {
    handleClose: () => void;
    handleChange: Dispatch<SetStateAction<CalendarEvent>>;
}

const EventCard: React.FC<EventCardProps> = ({
    id,
    title,
    start_time,
    end_time,
    description,
    location,
    all_day,
    editable = false,
    creater,
    capacity,
    image,
    handleClose,
    handleChange
}) => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const { user } = useAuth();
    const [alertInfo, setAlertInfo] = useState<{
        open: boolean;
        message: string;
        severity: "error" | "warning" | "info" | "success";
    }>({
        open: false,
        message: "",
        severity: "info",
    });

    const { setWarningDialog } = useApp();
    const IS_MANAGER = user.type === UserType.MANAGER;

    
    const handleExpand = () => {
        navigate(`/createEvent`, { state: { expanded: true, event: { title, start_time, end_time, description, location, all_day, editable, creater, capacity } } });
    }

    const handleValueChange = (key: string, value: any) => {
        handleChange((prev) => ({ ...prev, [key]: value }));
    }

    const validate = () => {
        let errors: { [key: string]: string } = {};
        if (!title) {
            errors.title = 'Title is required';
        }
        if (!description) {
            errors.description = 'Description is required';
        }
        if (!start_time) {
            errors.start = 'Start Date is required';
        }
        if (!end_time) {
            errors.end = 'End Date is required';
        }
        if (start_time < new Date()) {
            errors.start = 'Start Date should be greater than current date';
        }
        if (end_time < start_time) {
            errors.end = 'End Date should be greater than Start Date';
        }
        if (capacity < 0) {
            errors.capacity = 'Capacity should be greater than 0';
        }
        setErrors(errors);
        const buildMessage: string = Object.values(errors)[0];
        if (buildMessage) {
            showAlert(buildMessage, 'error');
        }

        return Object.keys(errors).length === 0;
    }

    const handleEdit = () => {
        navigate(`/createEvent`, { state: { edit: true, event: { id, title, start_time, end_time, description, location, all_day, editable, creater, capacity } } });
    }

    const handleDelete = () => {
        if (!id) return;
        setWarningDialog(true, `Are you sure you want to delete ${title} event?`, 'Delete Event', 
        () => {
            dispatch(Actions.event.deleteEventRequestAction.request({id}));
            setWarningDialog(false, '', '', () => {}, () => {});
        }, () => setWarningDialog(false, '', '', () => {}, () => {}));
    }

    const handleSave = () => {
        if (!validate()) return;
        dispatch(Actions.event.createEventRequestAction.request({
            eventData: {
                title,
                description,
                start_time,
                end_time,
                location,
                all_day,
                editable,
                capacity,
                creater: {
                    id: user.id,
                    name: user.first_name + ' ' + user.last_name,
                },
                image
            }
        }))
    }

    const showAlert = (message: string, severity: "error" | "warning" | "info" | "success") => {
        setAlertInfo({ open: true, message, severity });
        setTimeout(() => {
            handleCloseAlert();
        }
        , 6000);
    }

    const handleCloseAlert = () => {
        setAlertInfo((prev) => ({ ...prev, open: false }));
    };

    return (
        <Card elevation={3} sx={{ padding: 1, borderRadius: 3, width: 500 }}>
            <Stack direction='row' spacing={1} sx={{ justifyContent: 'flex-end' }}>
                {!editable && IS_MANAGER && <Tooltip title={"Edit Event"}><IconButton sx={{'&:hover': {color: 'primary.main'}}} onClick={handleEdit}><Edit sx={{ cursor: 'pointer' }} /></IconButton></Tooltip>}
                {!editable && IS_MANAGER && <Tooltip title={"Delete Event"}><IconButton sx={{'&:hover': {color: 'error.main'}}} onClick={handleDelete}><Delete sx={{ cursor: 'pointer' }} /></IconButton></Tooltip>}
                {editable && <Tooltip title={"Expand Event"}><IconButton sx={{'&:hover': {color: 'success.main'}}} onClick={handleExpand}><OpenInFull sx={{ cursor: 'pointer' }} /></IconButton></Tooltip>}
                <Tooltip title={"Close"}><IconButton sx={{'&:hover': {color: 'error.main'}}} onClick={handleClose}><Cancel sx={{ cursor: 'pointer' }} /></IconButton></Tooltip>
            </Stack>
            {alertInfo.open && (
                <Box sx={{ paddingBottom: '1rem' }}>
                    <Alert onClose={handleCloseAlert} severity={alertInfo.severity}>
                        {alertInfo.message}
                    </Alert>
                </Box>
            )}
            {!editable ? (
                <CardHeader
                    title={title}
                    subheader={`${new Date(start_time).toDateString()} . ${new Date(start_time).toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit', hour12: false })} - ${new Date(end_time).toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit', hour12: false })}`}
                />
            ) : (
                <CardHeader
                    title={
                        <TextField
                            label="Title"
                            value={title}
                            placeholder="Add Title"
                            variant="standard"
                            onChange={(e) => handleChange((prev) => ({ ...prev, title: e.target.value }))}
                            fullWidth
                            error={!!errors.title}
                            helperText={errors.title}
                        />
                    }
                    subheader={
                        <Box>
                            <Stack direction='row' sx={{mt:2}} spacing={1}>
                                <DatePickerComponent
                                    labelValue="Start Date"
                                    selectedDate={dayjs(start_time)}
                                    setSelectedDate={(date) => {
                                        if (date) {
                                            handleChange((prev) => ({ ...prev, start_time: date.toDate() }));
                                        }
                                      }}
                                    minDate={dayjs()}
                                />
                                <DatePickerComponent
                                    labelValue="End Date"
                                    selectedDate={dayjs(end_time)}
                                    setSelectedDate={(date) => {
                                        if (date) {
                                          handleChange((prev) => ({ ...prev, end_time: date.toDate() }));
                                        }
                                      }}
                                    minDate={dayjs(start_time)}
                                />
                            </Stack>
                            <Stack direction='row' sx={{mt:2}} spacing={1}>
                                <TimePicker
                                    label="Start Time"
                                    value={dayjs(start_time)}
                                    onChange={
                                        (date) => {
                                            if (date) 
                                            handleChange((prev) => ({ ...prev, start_time: date.toDate() }));
                                        }
                                    }
                                    ampm={false}
                                    minTime={dayjs(start_time).isSame(dayjs(), 'day') ? dayjs().add(5, 'minute') : undefined}
                                />
                                <TimePicker
                                    label="End Time"
                                    value={dayjs(end_time)}
                                    onChange={
                                        (date) => {
                                            if (date) 
                                            handleChange((prev) => ({ ...prev, end_time: date.toDate() }));
                                        }
                                    }
                                    ampm={false}
                                    minTime={dayjs(start_time).isSame(dayjs(end_time), 'day') ? dayjs(start_time).add(5, 'minute') : undefined}
                                />
                            </Stack>
                        </Box>
                    }
                />
            )}
            <CardContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    height: '100%',
                    maxHeight: 500, overflow: 'auto', 
                }}
            >
                {!editable ? location && <Stack sx={{mb: 2}} direction='row' spacing={1}>
                    <LocationOn />
                    <Typography>{location}</Typography>
                </Stack>: <TextField 
                    label="Location"
                    value={location}
                    placeholder="Add Location"
                    variant="outlined"
                    sx={{mb:2}}
                    onChange={(e) => handleValueChange('location', e.target.value)}
                    fullWidth
                />}
                {!editable ? capacity && <Stack sx={{mb: 2}} direction='row' spacing={1}>
                    <Groups2 />
                    <Typography>{capacity}</Typography>
                </Stack>: <TextField 
                    label="Capacity"
                    type="number"
                    slotProps={{ htmlInput: { min: 0 } }}
                    value={capacity}
                    placeholder="Add Capacity"
                    variant="outlined"
                    sx={{mb:2}}
                    onChange={(e) => handleValueChange('capacity', e.target.value)}
                    fullWidth
                />}
                <Stack sx={{mb:2, mt:0}} direction='row' spacing={1}>
                    {!editable && <Subject />}
                    <Box sx={{ width: !editable ? '90%' : '100%' }}>
                        <Editor
                            isReadOnly={!editable}
                            value={description}
                            onChange={(value) =>
                                handleChange((prevState) => ({
                                    ...prevState,
                                    description: value,
                                }))
                            }
                            noMinimumHeight={true}
                            shortToolbar={true}
                            placeholder="Add Description"
                            error={!!errors.description}
                            helperText={errors.description}
                        />
                    </Box>
                </Stack>
                {!editable && <Stack sx={{mb:2}} direction='row' spacing={1}>
                    <Event />
                    <Typography>{creater?.name}</Typography>
                </Stack>}
            </CardContent>
            {IS_MANAGER && editable && <CardActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button fullWidth onClick={handleClose} variant="contained" sx={{
                    backgroundColor: 'secondary.main',
                    '&:hover': {
                    backgroundColor: 'error.main'
                }}}>Close</Button>
                <Button fullWidth onClick={handleSave} variant="contained" sx={{
                    '&:hover': {
                        backgroundColor: 'success.main'
                    }
                }}>Save</Button>
            </CardActions>}
        </Card>
    );
};

export default EventCard;